import { useEffect, useState, useCallback } from "react";
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle, ExternalLink, Copy, Eye, Send, Loader2 } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { adminApi } from "../../lib/api";
import { TsolPaquetes } from "../../components/tsol/TsolPaquetes";
import { toast } from "sonner";

const JOBS = ["cartera-sync", "orders-sync", "weekly-digest", "tsol-daily", "daily_tsol_send"];

const formatCOP = (v) =>
  new Intl.NumberFormat("es-CO", { style: "currency", currency: "COP", maximumFractionDigits: 0 }).format(v || 0);

/**
 * Monitor de cron externos + envíos de Resend.
 *
 * Sirve para verificar dos cosas:
 *  1. ¿Cron-job.org está pegando realmente los endpoints /api/cron/*? → tabla "Hits"
 *  2. ¿Los envíos del resumen semanal salieron y qué status devolvió Resend? → tabla "Digest"
 *     Cada envío tiene un `resend_id` que se puede consultar en https://resend.com/emails/{id}
 */
export default function AdminCronMonitor() {
  const [hits, setHits] = useState([]);
  const [digests, setDigests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      adminApi.get("/admin/cron/hits", { params: { limit: 100 } }),
      adminApi.get("/admin/digest/logs", { params: { limit: 20 } }),
    ])
      .then(([h, d]) => {
        setHits(h.data.hits || []);
        setDigests(d.data.logs || []);
      })
      .catch(() => { setHits([]); setDigests([]); })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await adminApi.get("/admin/commercial/digest/preview");
      setPreview(data);
    } catch {
      toast.error("No pude cargar el preview del digest");
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const sendNow = useCallback(async () => {
    setSending(true);
    try {
      const { data } = await adminApi.post("/admin/commercial/digest/send-now");
      if (data.sent) {
        toast.success(`Digest enviado a ${data.to} · Resend ID: ${data.resend_id?.slice(0, 8) || "—"}...`);
      } else if (data.reason === "missing_api_key") {
        toast.error("Falta RESEND_API_KEY en el servidor de producción");
      } else if (data.reason === "missing_to_email") {
        toast.error("Falta CM_NOTIFICATION_EMAIL en el servidor de producción");
      } else {
        toast.error(`No se envió: ${data.reason || data.error || "error desconocido"}`);
      }
      load();
    } catch (e) {
      toast.error("Error al enviar: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  }, [load]);

  const filteredHits = filter ? hits.filter((h) => h.job === filter) : hits;
  const lastByJob = Object.fromEntries(
    JOBS.map((j) => [j, hits.find((h) => h.job === j && h.secret_ok)]),
  );

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copiado"); }
    catch { toast.error("No pude copiar"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-cron-monitor-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">Diagnóstico</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-1 tracking-tight">Cron & Resend</h1>
          <p className="text-sm text-cm-ink-soft mt-1">
            Confirma que cron-job.org está disparando los jobs y traza cada resumen semanal en Resend.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="border-cm-ink hover:bg-cm-ink hover:text-cm-cream" data-testid="refresh-cron-btn">
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refrescar
        </Button>
      </div>

      {/* Estado de cada job */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        {JOBS.map((job) => {
          const last = lastByJob[job];
          const hoursAgo = last
            ? (Date.now() - new Date(last.hit_at).getTime()) / 36e5
            : null;
          const stale = job === "weekly-digest" ? (hoursAgo === null || hoursAgo > 8 * 24) : (hoursAgo === null || hoursAgo > 36);
          return (
            <Card key={job} className={`p-4 border ${stale ? "border-cm-red bg-cm-red/5" : "border-cm-border bg-white"}`} data-testid={`job-card-${job}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-widest text-cm-ink-soft font-medium">{job}</div>
                {stale ? (
                  <Badge className="bg-cm-red text-white text-[10px]">Sin actividad</Badge>
                ) : (
                  <Badge className="bg-green-100 text-green-800 text-[10px] border border-green-300">
                    <CheckCircle2 className="h-3 w-3 mr-1 inline" /> Activo
                  </Badge>
                )}
              </div>
              <div className="font-mono text-sm text-cm-ink">
                {last ? new Date(last.hit_at).toLocaleString("es-CO") : "— nunca —"}
              </div>
              <div className="text-xs text-cm-ink-soft mt-1">
                {last ? (hoursAgo < 1 ? `hace ${Math.round(hoursAgo * 60)} min` : hoursAgo < 24 ? `hace ${hoursAgo.toFixed(1)} h` : `hace ${(hoursAgo / 24).toFixed(1)} días`) : "Nunca fue llamado por un cron externo."}
              </div>
              {(job === "tsol-daily" || job === "daily_tsol_send") && last?.result && (
                <div className="text-[11px] mt-2" data-testid={`job-tsol-last-result-${job}`}>
                  {last.result.status === "enviado" ? <span className="text-green-700">enviado ✓ · {last.result.zip_name} · {last.result.size_bytes} B · sha {String(last.result.sha256 || "").slice(0, 10)}…</span>
                    : last.result.status === "ya_enviado" ? <span className="text-green-700">ya enviado hoy ✓ {last.result.zip_name || ""}</span>
                    : last.result.status === "started" ? <span className="text-blue-700">en curso… (snapshot → ZIP → FTP)</span>
                    : last.result.status === "already_running" || last.result.status === "en_curso" ? <span className="text-cm-ink-soft">en curso (otra corrida) · {last.result.reason || ""}</span>
                    : last.result.status === "omitido" ? <span className="text-amber-700">omitido · {last.result.error || ""}</span>
                    : last.result.status === "fallido" ? <span className="text-cm-red">fallido ✗ · paso: {last.result.failed_step || "?"} · {last.result.error || ""}</span>
                    : last.result.error ? <span className="text-cm-red">{last.result.error}</span> : null}
                  <TsolPaquetes paquetes={last.result.paquetes} testId={`job-tsol-paquetes-${job}`} compact />
                </div>
              )}
              {last?.user_agent && (
                <div className="text-[10px] text-cm-ink-soft mt-2 truncate" title={last.user_agent}>
                  UA: {last.user_agent}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Preview del próximo digest — inspección directa del payload */}
      <Card className="bg-white border-cm-border shadow-none overflow-hidden" data-testid="digest-preview-card">
        <div className="p-4 border-b border-cm-border flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-serif text-lg flex items-center gap-2">
              <Eye className="h-4 w-4 text-cm-red" /> Preview del digest semanal
            </h2>
            <p className="text-xs text-cm-ink-soft mt-1">
              Datos que se enviarán al próximo envío (semana pasada, cerrando ayer). Si esto se ve vacío, el digest saldrá vacío — probablemente falta ejecutar <span className="font-mono">orders-sync</span> primero.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadPreview}
              disabled={previewLoading}
              data-testid="load-preview-btn"
              className="border-cm-border"
            >
              {previewLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
              Cargar preview
            </Button>
            <Button
              onClick={sendNow}
              disabled={sending}
              className="bg-cm-red hover:bg-cm-red-dark text-white"
              size="sm"
              data-testid="send-digest-btn"
            >
              {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
              Enviar ahora
            </Button>
          </div>
        </div>
        {preview ? (
          <div className="p-5 space-y-4" data-testid="digest-preview-content">
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-cm-cream-alt/50 border border-cm-border rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Período</div>
                <p className="font-mono text-xs mt-1">{preview.date_from} → {preview.date_to}</p>
                <p className="text-[10px] text-cm-ink-soft mt-0.5">Semana {preview.iso_week}</p>
              </div>
              <div className="bg-cm-cream-alt/50 border border-cm-border rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Ventas totales</div>
                <p className="font-serif text-xl mt-1">{formatCOP(preview.total)}</p>
                {preview.variation_pct !== null && (
                  <p className={`text-[10px] ${preview.variation_pct >= 0 ? "text-green-700" : "text-red-700"}`}>
                    {preview.variation_pct >= 0 ? "↑" : "↓"} {Math.abs(preview.variation_pct).toFixed(1)}% vs anterior
                  </p>
                )}
              </div>
              <div className="bg-cm-cream-alt/50 border border-cm-border rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Pedidos</div>
                <p className="font-serif text-xl mt-1">{preview.count}</p>
                <p className="text-[10px] text-cm-ink-soft">
                  {preview.breakdown?.portal?.count || 0} portal · {preview.breakdown?.presencial?.count || 0} presencial
                </p>
              </div>
              <div className="bg-cm-cream-alt/50 border border-cm-border rounded p-3">
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft">Ticket promedio</div>
                <p className="font-serif text-xl mt-1">{formatCOP(preview.avg_ticket)}</p>
              </div>
            </div>

            {/* Empty warning */}
            {preview.count === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 flex gap-2 items-start" data-testid="digest-empty-warning">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>El digest saldría vacío.</strong> Cero pedidos en el período. Verifica que <span className="font-mono">orders-sync</span> se haya ejecutado y que Arcos tenga pedidos en esas fechas.
                </div>
              </div>
            )}

            {/* Top lists preview */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft mb-1.5">🏆 Top 5 vendedores</div>
                {preview.top_vendedores?.length ? (
                  <ul className="text-sm space-y-1">
                    {preview.top_vendedores.slice(0, 5).map((v, i) => (
                      <li key={i} className="flex justify-between border-b border-cm-border py-1">
                        <span className="truncate pr-2">{i + 1}. {v.nombre}</span>
                        <span className="font-mono font-semibold whitespace-nowrap">{formatCOP(v.total)}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-cm-ink-soft">Sin datos</p>}
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-cm-ink-soft mb-1.5">🤝 Top 5 clientes</div>
                {preview.top_clientes?.length ? (
                  <ul className="text-sm space-y-1">
                    {preview.top_clientes.slice(0, 5).map((c, i) => (
                      <li key={i} className="flex justify-between border-b border-cm-border py-1">
                        <span className="truncate pr-2">{i + 1}. {c.nombre}</span>
                        <span className="font-mono font-semibold whitespace-nowrap">{formatCOP(c.total)}</span>
                      </li>
                    ))}
                  </ul>
                ) : <p className="text-xs text-cm-ink-soft">Sin datos</p>}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-cm-ink-soft">
            Presiona <strong>Cargar preview</strong> para ver los datos actuales del próximo digest sin enviarlo.
          </div>
        )}
      </Card>

      {/* Últimos hits crudos */}
      <Card className="bg-white border-cm-border shadow-none overflow-hidden">
        <div className="p-4 border-b border-cm-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <h2 className="font-serif text-lg">Hits recientes</h2>
            <p className="text-xs text-cm-ink-soft">Últimas 100 llamadas a <span className="font-mono">/api/cron/*</span>. Si esta lista no crece los lunes → cron-job.org NO está disparando.</p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm" variant={filter === "" ? "default" : "outline"}
              className={filter === "" ? "bg-cm-ink text-cm-cream" : ""}
              onClick={() => setFilter("")}
            >Todos</Button>
            {JOBS.map((j) => (
              <Button
                key={j} size="sm" variant={filter === j ? "default" : "outline"}
                className={filter === j ? "bg-cm-ink text-cm-cream text-xs" : "text-xs"}
                onClick={() => setFilter(j)}
              >{j}</Button>
            ))}
          </div>
        </div>
        {loading ? (
          <p className="p-8 text-cm-ink-soft text-center">Cargando…</p>
        ) : filteredHits.length === 0 ? (
          <div className="p-8 text-center" data-testid="hits-empty">
            <AlertTriangle className="h-8 w-8 text-cm-red mx-auto mb-2" />
            <p className="text-sm text-cm-ink">No hay hits registrados aún para este job.</p>
            <p className="text-xs text-cm-ink-soft mt-1">
              Confirma en cron-job.org que la URL y el secret sean correctos.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wider text-cm-ink-soft bg-cm-cream-alt/40">
                <tr>
                  <th className="px-4 py-3">Cuando</th>
                  <th className="px-4 py-3">Job</th>
                  <th className="px-4 py-3">Secret</th>
                  <th className="px-4 py-3">IP</th>
                  <th className="px-4 py-3">User Agent</th>
                  <th className="px-4 py-3">Resultado</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {filteredHits.map((h, i) => (
                  <tr key={i} className={`hover:bg-cm-cream-alt/30 ${!h.secret_ok ? "bg-cm-red/5" : ""}`}>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-cm-ink-soft whitespace-nowrap">
                      {new Date(h.hit_at).toLocaleString("es-CO")}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{h.job}</td>
                    <td className="px-4 py-2.5">
                      {h.secret_ok
                        ? <Badge variant="outline" className="bg-green-50 border-green-300 text-green-800 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> OK</Badge>
                        : <Badge variant="outline" className="bg-cm-red/10 border-cm-red text-cm-red text-[10px]"><XCircle className="h-3 w-3 mr-1" /> KO</Badge>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px] text-cm-ink-soft">{h.ip || "—"}</td>
                    <td className="px-4 py-2.5 text-xs text-cm-ink-soft truncate max-w-[280px]" title={h.user_agent}>{h.user_agent || "—"}</td>
                    <td className="px-4 py-2.5 text-xs font-mono">
                      {h.result?.sent === true ? <span className="text-green-700">sent ✓</span>
                        : h.result?.status === "enviado" ? <span className="text-green-700">enviado ✓ {h.result.zip_name} ({h.result.size_bytes} B)</span>
                        : h.result?.status === "ya_enviado" ? <span className="text-green-700">ya enviado hoy ✓</span>
                        : h.result?.status === "fallido" ? <span className="text-cm-red">fallido · paso {h.result.failed_step || "?"} · {h.result.error || ""}</span>
                        : h.result?.status === "en_curso" || h.result?.status === "already_running" ? <span className="text-cm-ink-soft">en curso (otra corrida)</span>
                        : h.result?.status === "omitido" ? <span className="text-amber-700">omitido · {h.result.error || ""}</span>
                        : h.result?.status === "started" ? <span className="text-blue-700">started</span>
                        : h.result?.status === "already_running" ? <span className="text-cm-ink-soft">running…</span>
                        : h.result?.error ? <span className="text-cm-red">{h.result.error}</span>
                        : "—"}
                      <TsolPaquetes paquetes={h.result?.paquetes} testId={`hit-tsol-paquetes-${h.id || h._id || h.hit_at}`} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Digest logs */}
      <Card className="bg-white border-cm-border shadow-none overflow-hidden">
        <div className="p-4 border-b border-cm-border">
          <h2 className="font-serif text-lg">Envíos del resumen semanal</h2>
          <p className="text-xs text-cm-ink-soft">
            Cada envío guarda el <span className="font-mono">resend_id</span> — cópialo y búscalo en{" "}
            <a href="https://resend.com/emails" target="_blank" rel="noreferrer" className="text-cm-red underline">resend.com/emails</a>{" "}
            para ver si Gmail lo entregó, rebotó o lo marcó como spam.
          </p>
        </div>
        {loading ? (
          <p className="p-8 text-cm-ink-soft text-center">Cargando…</p>
        ) : digests.length === 0 ? (
          <p className="p-8 text-cm-ink-soft text-center">No hay envíos aún.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="digest-logs-table">
              <thead className="text-left text-xs uppercase tracking-wider text-cm-ink-soft bg-cm-cream-alt/40">
                <tr>
                  <th className="px-4 py-3">Cuando</th>
                  <th className="px-4 py-3">Rango</th>
                  <th className="px-4 py-3">Destino</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Resend ID</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cm-border">
                {digests.map((d, i) => (
                  <tr key={i} className="hover:bg-cm-cream-alt/30">
                    <td className="px-4 py-2.5 font-mono text-[11px] text-cm-ink-soft whitespace-nowrap">
                      {new Date(d.sent_at).toLocaleString("es-CO")}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs">{d.date_from} → {d.date_to}</td>
                    <td className="px-4 py-2.5 text-xs">{d.to}</td>
                    <td className="px-4 py-2.5">
                      {d.ok
                        ? <Badge className="bg-green-100 border border-green-300 text-green-800 text-[10px]">{d.status_code}</Badge>
                        : <Badge className="bg-cm-red/10 border border-cm-red text-cm-red text-[10px]">{d.status_code || "err"}</Badge>}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[11px]">
                      {d.resend_id ? (
                        <div className="flex items-center gap-1">
                          <span className="text-cm-ink-soft">{d.resend_id.slice(0, 8)}…{d.resend_id.slice(-4)}</span>
                          <button
                            onClick={() => copy(d.resend_id)}
                            className="text-cm-ink-soft hover:text-cm-ink"
                            title="Copiar ID completo"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                          <a
                            href={`https://resend.com/emails/${d.resend_id}`}
                            target="_blank" rel="noreferrer"
                            className="text-cm-red hover:opacity-80"
                            title="Abrir en Resend dashboard"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-right font-mono">
                      ${(d.total || 0).toLocaleString("es-CO")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
